/**
 * Realtime subscriptions to declared read slices and definition changes over
 * the cell's multiplexed WebSocket (`GET /v1/subscriptions`): native `norn
 * dev`/celld and the Durable Objects host speak the same protocol.
 *
 * The frame and envelope types are the contract's (`ClientMessage`,
 * `ServerMessage`, `SliceUpdate`, and `DefinitionsUpdate` in the generated
 * OpenAPI types). This file adds only the connection lifecycle: reconnect
 * with backoff, resume every subscription from its last cursor, keepalive
 * pings, and duplicate-event dropping. v2 applies atomic row diffs against
 * the exact base cursor and requests a reset when the base cannot be proven. Subscriptions never
 * write; commands stay on the action endpoint with idempotency keys.
 */
import type { components } from "./generated/norn-api.ts"
import { matchesContent, parseSubscriptionJson, stringifySubscriptionJson } from "./subscription-content.ts"

type Schemas = components["schemas"]
/** One subscription event: the versioned envelope. */
export type SliceUpdate = Schemas["SliceUpdate"]
export type SlicePayload = components["schemas"]["SlicePayload"]
/** One versioned definitions-channel event. */
export type DefinitionsUpdate = Schemas["DefinitionsUpdate"]
/** A frame the cell sends. */
export type ServerMessage = Schemas["ServerMessage"]
/** A frame the client sends. */
export type ClientMessage = Schemas["ClientMessage"]

/** The envelope version this client speaks. */
export const PROTOCOL_VERSION = 2
const DEFINITIONS_PROTOCOL_VERSION = 1
/** The WebSocket subprotocol of subscription sockets. */
export const SUBPROTOCOL = "norn.subscriptions.v2"

/** The minimal WebSocket surface the client needs (the standard global by default). */
export interface SocketLike {
  readonly readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  addEventListener(type: "open" | "close" | "error", listener: () => void): void
  addEventListener(type: "message", listener: (event: { readonly data: unknown }) => void): void
}

export type SocketFactory = (url: string, protocols: string[]) => SocketLike

export interface SubscriptionClientOptions {
  /** The cell's base URL (`http://127.0.0.1:8787`); `ws`/`wss` is derived. */
  readonly baseUrl: string
  readonly orgId: string
  readonly environmentId: string
  /** The reading principal (`actor` parameter: browsers cannot set `x-norn-actor` on a socket). */
  readonly actor?: string
  /** A bearer token, offered as the `norn.bearer.<hex>` subprotocol (hex of its UTF-8). */
  readonly token?: string
  /** Defaults to the global `WebSocket`. */
  readonly socket?: SocketFactory
  /** Reconnect backoff bounds in milliseconds (default 250 to 30 000). */
  readonly backoff?: { readonly initialMs: number; readonly maxMs: number }
  /** Keepalive ping interval (default 25 000 ms); a silent socket is replaced after two intervals. */
  readonly pingMs?: number
  /** Timer seam for tests. */
  readonly timers?: Timers
}

export interface Timers {
  setTimeout(callback: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
}

/** A refused subscribe: the cell's stable code and explanation. */
export interface SubscriptionRefusal {
  readonly code: string
  readonly explanation: string
}

/** Binding declarations own filters and limits; callers supply only context and lifecycle controls. */
export type SubscribeBindingOptions = Omit<SubscribeOptions, "filter" | "limit"> & {
  readonly filter?: never
  readonly limit?: never
  readonly params?: Record<string, string>
}

export interface SubscribeOptions {
  /** Equality predicates by property id. */
  readonly filter?: Record<string, unknown>
  /** The slice bound (the cell's default when absent). */
  readonly limit?: number
  /** Resume from the cursor of the last event applied (for example, persisted across reloads). */
  readonly after?: string
  /** State matching `after`, when resuming across a reload. Otherwise request a snapshot. */
  readonly initial?: SlicePayload
  /** Complete locally applied result after a snapshot or matching diff. */
  /** Retained projection was discarded; clear every displayed copy before recovery. */
  readonly onInvalidated?: () => void
  readonly onResult?: (result: SlicePayload, event: SliceUpdate) => void
  /** Each event whose content is new to this subscription, in log order. */
  readonly onEvent: (event: SliceUpdate) => void
  /** Terminal refusals are not retried. Bounded authorization-index loading retries automatically. */
  readonly onRefused?: (refusal: SubscriptionRefusal) => void
}

/** Subscribe to changes in the active definitions set. */
export interface SubscribeDefinitionsOptions {
  /** The definitions world (defaults to `main`). */
  readonly world?: string
  /** Resume from the cursor of the last event applied (for example, persisted across reloads). */
  readonly after?: string
  /** New revision notices and resyncs; a different cell history can reset the revision. */
  readonly onEvent: (event: DefinitionsUpdate) => void
  /** The cell refused the subscription; it is not retried. */
  readonly onRefused?: (refusal: SubscriptionRefusal) => void
}

/** A live subscription handle. */
export interface SubscriptionHandle {
  /** The last delivered cursor; persist after applying the event or completing its refetch. */
  readonly cursor: string | undefined
  /** Current applied object/binding page, absent after revocation or reset request. */
  readonly result: SlicePayload | undefined
  unsubscribe(): void
}

interface EntryBase {
  readonly ref: string
  subscriptionId: number | undefined
  cursor: string | undefined
  active: boolean
  admissionRetry: { handle: unknown } | undefined
  admissionAttempt: number
}

interface ObjectEntry extends EntryBase {
  readonly channel: "object"
  readonly objectType: string
  readonly options: SubscribeOptions
  eventId: string | undefined
  result: SlicePayload | undefined
  expectedResume: string | undefined
  binding: { widgetId: string; bindingId: string; params?: Record<string, string> } | undefined
  readonly logHistory: Map<number, string>
}

interface DefinitionsEntry extends EntryBase {
  readonly channel: "definitions"
  readonly world: string
  readonly options: SubscribeDefinitionsOptions
  historyKey: string | undefined
  latestRevision: number | undefined
  lastEventCursor: string | undefined
}

type Entry = ObjectEntry | DefinitionsEntry

const realTimers: Timers = {
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>),
}

const OPEN = 1

/** A token as protocol-token-safe hex of its UTF-8 bytes. */
function hexOf(token: string): string {
  return Array.from(new TextEncoder().encode(token), (byte) => byte.toString(16).padStart(2, "0")).join("")
}

const PAYLOAD_KINDS: ReadonlySet<string> = new Set(["snapshot", "upsert", "remove", "reset"])
const KINDS: ReadonlySet<string> = new Set([...PAYLOAD_KINDS, "resumed", "stale", "closed"])
const REASONS: ReadonlySet<string> = new Set(["slow_consumer", "definitions_changed", "authorization_changed", "dependency_budget_exceeded"])
const CURSOR = /^n1\.(\d+)\.[0-9a-f]{16}\.[0-9a-f]{16}\.(\d+)\.[0-9a-f]{16}$/
const DEFINITION_REASONS: ReadonlySet<string> = new Set([
  "gap",
  "overflow",
  "cursor_mismatch",
  "authorization_changed",
])
const DEFINITION_CURSOR = /^nd1\.(\d+)\.([0-9a-f]{16})\.([0-9a-f]{16})\.[0-9a-f]{16}$/

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null
  && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
const isPosition = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0

function isPayload(value: unknown, cellSeq: number): boolean {
  if (!isRecord(value) || !Array.isArray(value.items) || typeof value.complete !== "boolean") return false
  if (!isPosition(value.commitSeq) || value.commitSeq > cellSeq) return false
  return value.items.every(
    (item) => isRecord(item) && typeof item.objectType === "string" && typeof item.key === "string" && isPosition(item.version) && isRecord(item.values),
  )
}

/**
 * Whether `value` is a well-formed version-2 object envelope (the runtime boundary
 * check): kinds and reasons, non-negative integer positions, a cursor that
 * names the event's position, and a payload of the contract's shape exactly
 * when the kind carries one.
 */
export function isSliceUpdate(value: unknown): value is SliceUpdate {
  if (!isRecord(value) || value.v !== PROTOCOL_VERSION) return false
  const { kind, cellSeq, resultVersion, resultCursor: cursor, payload, reason } = value
  if (typeof kind !== "string" || !KINDS.has(kind)) return false
  if (!isPosition(value.subscriptionId) || !isPosition(cellSeq) || !isPosition(resultVersion) || resultVersion > cellSeq) return false
  if (typeof value.eventId !== "string" || typeof cursor !== "string") return false
  const position = CURSOR.exec(cursor)
  if (!position || Number(position[1]) !== cellSeq || Number(position[2]) !== resultVersion) return false
  const expectedId = kind === "stale" || kind === "closed" ? `${kind}:${cellSeq}` : `result:${cursor.split(".")[5]}`
  if (value.eventId !== expectedId) return false
  const carries = payload !== undefined && payload !== null
  if (PAYLOAD_KINDS.has(kind) !== carries || (carries && !isPayload(payload, cellSeq))) return false
  if (carries && (!isRecord(payload) || payload.commitSeq !== resultVersion)) return false
  const diff = kind === "upsert" || kind === "remove"
  if (diff !== (typeof value.baseCursor === "string")) return false
  if (diff) {
    const base = CURSOR.exec(value.baseCursor as string)
    if (!base || !isPosition(Number(base[1])) || !isPosition(Number(base[2])) || Number(base[1]) > cellSeq || Number(base[2]) > resultVersion || Number(base[2]) > Number(base[1])) return false
    const parts = cursor.split(".")
    if ((value.baseCursor as string).split(".")[3] !== parts[3]) return false
    if (!isRecord(payload)) return false
    const keys = (value: unknown): value is Array<{ objectType: string; key: string }> => Array.isArray(value) && value.every(
      (key) => isRecord(key) && typeof key.objectType === "string" && typeof key.key === "string",
    )
    if ((payload.removed !== undefined && !keys(payload.removed)) || (payload.order !== undefined && !keys(payload.order))) return false
    if (kind === "remove" && (payload.items as unknown[]).length !== 0) return false
  } else if (isRecord(payload) && ((payload.removed !== undefined && (!Array.isArray(payload.removed) || payload.removed.length !== 0)) || (payload.order !== undefined && (!Array.isArray(payload.order) || payload.order.length !== 0)))) {
    return false
  }
  const closes = reason !== undefined && reason !== null
  return (kind === "closed") === closes && (!closes || (typeof reason === "string" && REASONS.has(reason)))
}

/** Apply a snapshot or exact-base diff after asynchronous WebCrypto content verification; undefined asks for reset.
 * `logHistory` contains verified receipt digests by cell position for the base binding's history.
 */
export async function applySliceUpdate(previous: SlicePayload | undefined, cursor: string | undefined, event: SliceUpdate, logHistory?: ReadonlyMap<number, string>): Promise<SlicePayload | undefined> {
  if (!isSliceUpdate(event)) return undefined
  const payload = event.payload
  if (payload === undefined || payload === null) {
    if (event.kind !== "resumed" || previous === undefined || cursor === undefined) return undefined
    const base = CURSOR.exec(cursor)
    const next = CURSOR.exec(event.resultCursor)
    if (!base || !next || Number(next[1]) < Number(base[1]) || previous.commitSeq !== event.resultVersion
      || cursor.split(".")[3] !== event.resultCursor.split(".")[3]
      || base[2] !== next[2] || cursor.split(".")[5] !== event.resultCursor.split(".")[5]
      || !isPayload(previous, event.cellSeq) || !await matchesContent(previous, cursor)
      || !await matchesContent(previous, event.resultCursor)) return undefined
    return copyPayload(previous)
  }
  if (event.kind === "snapshot" || event.kind === "reset") {
    if (cursor !== undefined) {
      const base = CURSOR.exec(cursor)
      const next = CURSOR.exec(event.resultCursor)
      if (!base || !next) return undefined
      // Receipt digests change at each position. Compare against the retained digest at the
      // incoming position, never against the current head's digest. An unknown position
      // cannot prove a history change and requires cursorless recovery on regression.
      const knownLog = logHistory?.get(Number(next[1])) ?? (next[1] === base[1] ? cursor.split(".")[2] : undefined)
      const changedHistory = knownLog !== undefined && knownLog !== event.resultCursor.split(".")[2]
      if (cursor.split(".")[3] === event.resultCursor.split(".")[3]
        && !changedHistory
        && (Number(next[1]) < Number(base[1]) || Number(next[2]) < Number(base[2]))) return undefined
    }
    const owned = copyPayload(payload)
    return await matchesContent(owned, event.resultCursor) ? owned : undefined
  }
  if (event.baseCursor !== cursor || previous === undefined) return undefined
  const identity = (row: { objectType: string; key: string }) => JSON.stringify([row.objectType, row.key])
  const rows = new Map(previous.items.map((item) => [identity(item), item]))
  const removed = new Set<string>()
  for (const key of payload.removed ?? []) {
    const id = identity(key)
    if (removed.has(id) || !rows.delete(id)) return undefined
    removed.add(id)
  }
  const upserts = new Set<string>()
  for (const item of payload.items) {
    const id = identity(item)
    if (removed.has(id) || upserts.has(id)) return undefined
    upserts.add(id)
    rows.set(id, item)
  }
  const items: SlicePayload["items"] = []
  for (const key of payload.order ?? []) {
    const id = identity(key)
    const item = rows.get(id)
    if (item === undefined) return undefined
    items.push(item)
    rows.delete(id)
    upserts.delete(id)
  }
  if (rows.size !== 0 || upserts.size !== 0) return undefined
  const result = { items, complete: payload.complete, commitSeq: payload.commitSeq }
  const owned = copyPayload(result)
  return await matchesContent(owned, event.resultCursor) ? owned : undefined
}

/** Lossless codec copies nested JSON and exact-number objects without exposing retained diff bases. */
function copyPayload(payload: SlicePayload): SlicePayload {
  return parseSubscriptionJson(stringifySubscriptionJson(payload)) as SlicePayload
}

/**
 * Whether `value` is a well-formed version-1 definitions event. The cursor
 * must name the revision; event-specific revision and reason invariants are
 * checked before the event reaches a listener.
 */
export function isDefinitionsUpdate(value: unknown): value is DefinitionsUpdate {
  if (!isRecord(value) || value.v !== DEFINITIONS_PROTOCOL_VERSION || value.channel !== "definitions") return false
  if (
    !isPosition(value.subscriptionId) ||
    typeof value.orgId !== "string" ||
    value.orgId.length === 0 ||
    typeof value.environmentId !== "string" ||
    value.environmentId.length === 0 ||
    typeof value.world !== "string" ||
    value.world.length === 0 ||
    !isPosition(value.revision) ||
    !isPosition(value.previousRevision) ||
    !Array.isArray(value.changedArtifactIds) ||
    !value.changedArtifactIds.every((id) => typeof id === "string" && id.length > 0) ||
    typeof value.resumeCursor !== "string"
  ) {
    return false
  }
  const cursor = DEFINITION_CURSOR.exec(value.resumeCursor)
  if (!cursor || !Number.isSafeInteger(Number(cursor[1])) || Number(cursor[1]) !== value.revision) return false

  const reason = value.reason
  const hasReason = reason !== undefined && reason !== null
  if (hasReason && (typeof reason !== "string" || !DEFINITION_REASONS.has(reason))) return false

  switch (value.kind) {
    case "installed":
      return !hasReason && value.previousRevision < Number.MAX_SAFE_INTEGER && value.revision === value.previousRevision + 1
    case "resumed":
      return !hasReason && value.previousRevision === value.revision && value.changedArtifactIds.length === 0
    case "resync_required":
      return (
        hasReason &&
        reason !== "authorization_changed" &&
        value.changedArtifactIds.length === 0
      )
    case "closed":
      return (
        reason === "authorization_changed" &&
        value.previousRevision === value.revision &&
        value.changedArtifactIds.length === 0
      )
    default:
      return false
  }
}

function definitionsCursorState(cursor: string | undefined): { historyKey: string | undefined; revision: number | undefined } {
  if (cursor === undefined) return { historyKey: undefined, revision: undefined }
  const parsed = DEFINITION_CURSOR.exec(cursor)
  if (!parsed) return { historyKey: undefined, revision: undefined }
  const revision = Number(parsed[1])
  if (!Number.isSafeInteger(revision)) return { historyKey: undefined, revision: undefined }
  return { historyKey: `${parsed[2]}.${parsed[3]}`, revision }
}

/**
 * One multiplexed subscription connection. It reconnects with jittered
 * exponential backoff and resumes each subscription from its cursor; the
 * cell answers `resumed` (unchanged) or `reset` (replace local
 * state: the cursor no longer applies, for example after the reader changed).
 * Definitions channels receive `installed`, `resumed`, `resync_required`, or
 * a terminal `closed` notice when the read permission is revoked; the caller
 * fetches and reconciles definition bodies.
 */
export class SubscriptionClient {
  readonly #options: SubscriptionClientOptions
  readonly #timers: Timers
  readonly #entries = new Map<string, Entry>()
  /** Refs unsubscribed before their acknowledgement named the subscription. */
  readonly #cancelled = new Set<string>()
  #socket: SocketLike | undefined
  #nextRef = 1
  #attempt = 0
  #closed = false
  #retry: unknown
  #keepalive: unknown
  #receiving: Promise<void> = Promise.resolve()
  #heardSincePing = true

  constructor(options: SubscriptionClientOptions) {
    this.#options = options
    this.#timers = options.timers ?? realTimers
    this.#connect()
  }

  /** Subscribe to a declared slice of `objectType`. */
  subscribe(objectType: string, options: SubscribeOptions): SubscriptionHandle {
    return this.#subscribe(objectType, options, undefined)
  }

  #subscribe(objectType: string, options: SubscribeOptions, binding: ObjectEntry["binding"]): SubscriptionHandle {
    const ref = `s${this.#nextRef++}`
    const entry: ObjectEntry = {
      ref,
      channel: "object",
      objectType,
      options,
      subscriptionId: undefined,
      cursor: options.initial === undefined ? undefined : options.after,
      eventId: undefined,
      expectedResume: undefined,
      result: options.initial === undefined ? undefined : copyPayload(options.initial),
      binding,
      logHistory: new Map(),
      active: true,
      admissionRetry: undefined,
      admissionAttempt: 0,
    }
    this.#entries.set(ref, entry)
    this.#sendSubscribe(entry)
    return this.#handle(entry)
  }

  /** Subscribe to the admitted WidgetIR binding head page. Context is validated by the cell. */
  subscribeBinding(widgetId: string, bindingId: string, options: SubscribeBindingOptions): SubscriptionHandle {
    return this.#subscribe("", options, options.params === undefined ? { widgetId, bindingId } : { widgetId, bindingId, params: options.params })
  }

  /** Subscribe to versioned changes in the active definitions set. */
  subscribeDefinitions(options: SubscribeDefinitionsOptions): SubscriptionHandle {
    const ref = `s${this.#nextRef++}`
    const cursorState = definitionsCursorState(options.after)
    const entry: DefinitionsEntry = {
      ref,
      channel: "definitions",
      world: options.world ?? "main",
      options,
      subscriptionId: undefined,
      cursor: options.after,
      historyKey: cursorState.historyKey,
      latestRevision: cursorState.revision,
      lastEventCursor: undefined,
      active: true,
      admissionRetry: undefined,
      admissionAttempt: 0,
    }
    this.#entries.set(ref, entry)
    this.#sendSubscribe(entry)
    return this.#handle(entry)
  }

  #handle(entry: Entry): SubscriptionHandle {
    const client = this
    return {
      get cursor() {
        return entry.cursor
      },
      get result() {
        return entry.channel === "object" && entry.result !== undefined ? copyPayload(entry.result) : undefined
      },
      unsubscribe() {
        client.#unsubscribe(entry)
      },
    }
  }

  /** Close the connection and every subscription. */
  close(): void {
    this.#closed = true
    this.#timers.clearTimeout(this.#retry)
    this.#timers.clearTimeout(this.#keepalive)
    const entries = [...this.#entries.values()]
    // Clear every protected base and end transport before application callbacks can throw.
    for (const entry of entries) this.#clear(entry)
    this.#entries.clear()
    this.#socket?.close(1000, "closed by the client")
    this.#socket = undefined
    const failures: unknown[] = []
    for (const entry of entries) {
      if (entry.channel === "object") {
        try { entry.options.onInvalidated?.() } catch (error) { failures.push(error) }
      }
    }
    if (failures.length > 0) throw new AggregateError(failures, "subscription invalidation callbacks failed")
  }

  #url(): string {
    const url = new URL("/v1/subscriptions", this.#options.baseUrl)
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:"
    url.searchParams.set("orgId", this.#options.orgId)
    url.searchParams.set("environmentId", this.#options.environmentId)
    if (this.#options.actor !== undefined) url.searchParams.set("actor", this.#options.actor)
    return url.toString()
  }

  #connect(): void {
    if (this.#closed) return
    const protocols = [SUBPROTOCOL]
    if (this.#options.token !== undefined) protocols.push(`norn.bearer.${hexOf(this.#options.token)}`)
    const factory: SocketFactory = this.#options.socket ?? ((url, offered) => new WebSocket(url, offered) as unknown as SocketLike)
    const socket = factory(this.#url(), protocols)
    this.#socket = socket
    socket.addEventListener("open", () => {
      if (this.#socket !== socket) return
      this.#attempt = 0
      // A fresh socket starts live; only its own silence replaces it.
      this.#heardSincePing = true
      // A new connection holds none of the old subscriptions.
      this.#cancelled.clear()
      for (const entry of this.#entries.values()) {
        entry.subscriptionId = undefined
        this.#sendSubscribe(entry)
      }
      this.#scheduleKeepalive()
    })
    socket.addEventListener("message", (event) => {
      if (this.#socket !== socket) return
      this.#heardSincePing = true
      if (typeof event.data === "string") {
        const text = event.data
        // WebCrypto is asynchronous: serialize frames so a diff cannot overtake its base.
        const receive = async () => {
          if (this.#socket === socket) await this.#receive(text)
        }
        this.#receiving = this.#receiving.then(receive, receive)
        return this.#receiving
      }
    })
    const lost = () => {
      if (this.#socket !== socket) return
      this.#socket = undefined
      this.#scheduleReconnect()
    }
    socket.addEventListener("close", lost)
    socket.addEventListener("error", lost)
  }

  #scheduleReconnect(): void {
    if (this.#closed) return
    for (const entry of this.#entries.values()) this.#cancelAdmissionRetry(entry)
    this.#timers.clearTimeout(this.#keepalive)
    const { initialMs, maxMs } = this.#options.backoff ?? { initialMs: 250, maxMs: 30_000 }
    const ceiling = Math.min(maxMs, initialMs * 2 ** this.#attempt)
    this.#attempt += 1
    // Full jitter keeps a fleet of clients from reconnecting in lockstep.
    this.#retry = this.#timers.setTimeout(() => this.#connect(), Math.random() * ceiling)
  }

  #scheduleKeepalive(): void {
    const interval = this.#options.pingMs ?? 25_000
    this.#timers.clearTimeout(this.#keepalive)
    this.#keepalive = this.#timers.setTimeout(() => {
      const socket = this.#socket
      if (!socket) return
      if (!this.#heardSincePing) {
        // Nothing since the last ping: replace the socket.
        this.#socket = undefined
        socket.close(4000, "keepalive timeout")
        this.#scheduleReconnect()
        return
      }
      this.#heardSincePing = false
      this.#send({ type: "ping" })
      this.#scheduleKeepalive()
    }, interval)
  }

  #send(message: ClientMessage): void {
    const socket = this.#socket
    if (socket && socket.readyState === OPEN) socket.send(stringifySubscriptionJson(message))
  }

  #sendSubscribe(entry: Entry): void {
    this.#cancelAdmissionRetry(entry)
    if (entry.channel === "definitions") {
      const message: Record<string, unknown> = {
        type: "subscribe",
        channel: "definitions",
        ref: entry.ref,
        world: entry.world,
      }
      if (entry.cursor !== undefined) message.after = entry.cursor
      this.#send(message as unknown as ClientMessage)
      return
    }
    entry.expectedResume = entry.cursor
    const { filter, limit } = entry.options
    const message: Record<string, unknown> = entry.binding === undefined
      ? { type: "subscribe", ref: entry.ref, objectType: entry.objectType }
      : { type: "subscribe_binding", ref: entry.ref, ...entry.binding }
    if (entry.binding === undefined && filter !== undefined) message.filter = filter
    if (entry.binding === undefined && limit !== undefined) message.limit = limit
    if (entry.cursor !== undefined) message.after = entry.cursor
    this.#send(message as unknown as ClientMessage)
  }

  #unsubscribe(entry: Entry): void {
    if (!entry.active) return
    this.#cancelAdmissionRetry(entry)
    entry.active = false
    this.#entries.delete(entry.ref)
    entry.cursor = undefined
    if (entry.channel === "object") entry.result = undefined
    if (entry.subscriptionId !== undefined) this.#send({ type: "unsubscribe", subscriptionId: entry.subscriptionId } as ClientMessage)
    // Not yet acknowledged: end it when the acknowledgement names it.
    else this.#cancelled.add(entry.ref)
  }

  #byId(id: number): Entry | undefined {
    for (const entry of this.#entries.values()) if (entry.subscriptionId === id) return entry
    return undefined
  }

  async #receive(text: string): Promise<void> {
    let frame: Record<string, unknown>
    try {
      frame = parseSubscriptionJson(text) as Record<string, unknown>
    } catch {
      return
    }
    if (!isRecord(frame)) return
    switch (frame.type) {
      case "subscribed": {
        if (typeof frame.ref !== "string" || !isPosition(frame.subscriptionId)) return
        if (this.#cancelled.delete(frame.ref)) {
          this.#send({ type: "unsubscribe", subscriptionId: frame.subscriptionId } as ClientMessage)
          return
        }
        const entry = this.#entries.get(frame.ref)
        if (entry) {
          this.#cancelAdmissionRetry(entry)
          entry.admissionAttempt = 0
          entry.subscriptionId = frame.subscriptionId
        }
        return
      }
      case "refused": {
        if (typeof frame.ref === "string") this.#cancelled.delete(frame.ref)
        const entry = typeof frame.ref === "string" ? this.#entries.get(frame.ref) : undefined
        if (!entry) return
        if (frame.code === "subscription.authorization_pending" && entry.channel === "object" && entry.subscriptionId === undefined) {
          // Admission has not authorized the retained projection. Drop it once,
          // then advance bounded index loading through the same retry scheduler.
          if (entry.result !== undefined || entry.cursor !== undefined) this.#reset(entry, true)
          else this.#scheduleAdmissionRetry(entry)
          return
        }
        this.#entries.delete(entry.ref)
        this.#clear(entry)
        try { if (entry.channel === "object") entry.options.onInvalidated?.() }
        finally { entry.options.onRefused?.({ code: String(frame.code), explanation: String(frame.explanation) }) }
        return
      }
      case "event": {
        if (!isSliceUpdate(frame.event)) {
          if (isRecord(frame.event) && isPosition(frame.event.subscriptionId)) {
            const entry = this.#byId(frame.event.subscriptionId)
            if (entry?.channel === "object") this.#reset(entry)
          }
          return
        }
        await this.#apply(frame.event)
        return
      }
      case "definitions_event": {
        if (!isDefinitionsUpdate(frame.event)) return
        this.#applyDefinitions(frame.event)
        return
      }
      default:
        return
    }
  }

  #clear(entry: Entry): void {
    this.#cancelAdmissionRetry(entry)
    entry.active = false
    entry.subscriptionId = undefined
    entry.cursor = undefined
    if (entry.channel === "object") {
      entry.result = undefined
      entry.eventId = undefined
      entry.expectedResume = undefined
      entry.logHistory.clear()
    }
  }

  #cancelAdmissionRetry(entry: Entry): void {
    if (entry.admissionRetry !== undefined) this.#timers.clearTimeout(entry.admissionRetry.handle)
    entry.admissionRetry = undefined
  }

  #scheduleAdmissionRetry(entry: Entry): void {
    if (entry.admissionRetry !== undefined || !entry.active || this.#closed) return
    const socket = this.#socket
    if (!socket || socket.readyState !== OPEN) return
    // Each refusal admits one bounded authorization-index page. Keep the caller
    // loading with one retry at a time; default backoff is capped at 1000 ms.
    const delay = (this.#options.backoff?.initialMs ?? 250) * 2 ** Math.min(entry.admissionAttempt, 2)
    entry.admissionAttempt = Math.min(entry.admissionAttempt + 1, 2)
    const retry = { handle: undefined as unknown }
    entry.admissionRetry = retry
    retry.handle = this.#timers.setTimeout(() => {
      if (entry.admissionRetry !== retry) return
      entry.admissionRetry = undefined
      if (entry.active && !this.#closed && this.#socket === socket && socket.readyState === OPEN) this.#sendSubscribe(entry)
    }, delay)
  }

  #reset(entry: ObjectEntry, retryAdmission = false): void {
    if (entry.subscriptionId !== undefined) this.#send({ type: "unsubscribe", subscriptionId: entry.subscriptionId } as ClientMessage)
    entry.subscriptionId = undefined
    entry.cursor = undefined
    entry.result = undefined
    entry.eventId = undefined
    entry.expectedResume = undefined
    entry.logHistory.clear()
    this.#cancelAdmissionRetry(entry)
    if (entry.active) {
      if (retryAdmission) this.#scheduleAdmissionRetry(entry)
      else this.#sendSubscribe(entry)
    }
    entry.options.onInvalidated?.()
  }

  async #apply(event: SliceUpdate): Promise<void> {
    const entry = this.#byId(event.subscriptionId)
    const socket = this.#socket
    if (!entry || entry.channel !== "object" || !entry.active) return
    if (event.kind === "closed") {
      entry.subscriptionId = undefined
      // A terminal notice carries a transport position, not a client ack.
      // Keep only the last cursor whose state we actually applied.
      if (event.reason !== "slow_consumer") {
        entry.result = undefined
        entry.cursor = undefined
        entry.eventId = undefined
        entry.expectedResume = undefined
        entry.logHistory.clear()
      }
      if (event.reason === "dependency_budget_exceeded") {
        this.#clear(entry)
        this.#entries.delete(entry.ref)
      } else if (entry.active) this.#sendSubscribe(entry)
      // User callbacks cannot interrupt terminal cleanup or recovery.
      try { if (event.reason !== "slow_consumer") entry.options.onInvalidated?.() }
      finally {
        try { entry.options.onEvent(event) }
        finally {
          if (event.reason === "dependency_budget_exceeded") {
            entry.options.onRefused?.({ code: "subscription.dependency_budget_exceeded", explanation: "the query dependency proof exceeded its budget" })
          }
        }
      }
      return
    }
    if (event.kind === "stale") {
      // Refresh head: stale positions must not become diff bases.
      this.#reset(entry)
      entry.options.onEvent(event)
      return
    }
    if (event.kind === "resumed") {
      // Resumes belong only to the outstanding handshake. Replays after a diff are inert.
      if (entry.expectedResume === undefined) {
        if (entry.result === undefined || entry.cursor === undefined || entry.eventId === undefined) this.#reset(entry)
        return
      }
      const resumed = await applySliceUpdate(entry.result, entry.expectedResume, event)
      if (!entry.active || entry.subscriptionId !== event.subscriptionId || this.#socket !== socket) return
      if (resumed === undefined) {
        this.#reset(entry)
        return
      }
      this.#rememberLog(entry, entry.expectedResume)
      entry.expectedResume = undefined
      entry.result = resumed
      entry.cursor = event.resultCursor
      entry.eventId = event.eventId
      this.#rememberLog(entry, event.resultCursor)
      entry.options.onEvent(event)
      return
    }
    const replacement = event.kind === "reset" || event.kind === "snapshot"
    if (event.eventId === entry.eventId && !replacement) return
    const result = await applySliceUpdate(entry.result, entry.cursor, event, entry.logHistory)
    if (!entry.active || entry.subscriptionId !== event.subscriptionId || this.#socket !== socket) return
    if (result === undefined) { this.#reset(entry); return }
    entry.expectedResume = undefined
    // Recovery can replay a replacement after sending but before persisting
    // the session cursor. Only a previously verified exact cursor is a replay;
    // newer replacements may have the same content digest/eventId.
    if (replacement && event.resultCursor === entry.cursor && event.eventId === entry.eventId) return
    this.#rememberLog(entry, event.resultCursor)
    entry.result = result
    entry.cursor = event.resultCursor
    entry.eventId = event.eventId
    try { entry.options.onResult?.(copyPayload(result), event) }
    finally { entry.options.onEvent(event) }
  }

  #rememberLog(entry: ObjectEntry, cursor: string): void {
    const parts = cursor.split(".")
    const position = Number(parts[1])
    const knownLog = entry.logHistory.get(position)
    if ((entry.cursor !== undefined && entry.cursor.split(".")[3] !== parts[3])
      || (knownLog !== undefined && knownLog !== parts[2])) entry.logHistory.clear()
    entry.logHistory.set(position, parts[2]!)
    // Retain a bounded window of verified receipt positions. Older unknown regressions
    // fall back to cursorless recovery rather than weakening monotonicity checks.
    if (entry.logHistory.size > 128) entry.logHistory.delete(entry.logHistory.keys().next().value!)
  }

  #applyDefinitions(event: DefinitionsUpdate): void {
    const entry = this.#byId(event.subscriptionId)
    if (
      !entry ||
      entry.channel !== "definitions" ||
      !entry.active ||
      event.orgId !== this.#options.orgId ||
      event.environmentId !== this.#options.environmentId ||
      event.world !== entry.world
    ) {
      return
    }
    const cursorParts = DEFINITION_CURSOR.exec(event.resumeCursor)
    if (!cursorParts) return
    if (event.kind === "closed") {
      entry.cursor = event.resumeCursor
      entry.subscriptionId = undefined
      entry.active = false
      this.#entries.delete(entry.ref)
      entry.options.onEvent(event)
      return
    }
    const historyKey = `${cursorParts[2]}.${cursorParts[3]}`
    if (entry.historyKey === undefined) {
      entry.historyKey = historyKey
    } else if (entry.historyKey !== historyKey) {
      if (event.kind !== "resync_required") return
      // Revision numbers belong to one definitions history. A resync can
      // move to a new cell incarnation where its revision starts over.
      entry.historyKey = historyKey
      entry.latestRevision = undefined
      entry.lastEventCursor = undefined
    }
    if (
      event.kind !== "resync_required" &&
      entry.latestRevision !== undefined &&
      (event.revision < entry.latestRevision || (event.kind === "installed" && event.revision <= entry.latestRevision))
    ) {
      return
    }
    if (event.kind !== "resync_required" && entry.lastEventCursor === event.resumeCursor) return
    entry.cursor = event.resumeCursor
    entry.lastEventCursor = event.resumeCursor
    entry.latestRevision = event.revision
    if (entry.active && event.kind === "resync_required" && event.reason === "overflow") {
      // Overflow closes this channel. Resume from the reported head after
      // recording it, before a caller's callback can throw.
      entry.subscriptionId = undefined
      this.#sendSubscribe(entry)
    }
    entry.options.onEvent(event)
  }
}

/** Open a subscription connection to one cell. */
export function connectSubscriptions(options: SubscriptionClientOptions): SubscriptionClient {
  return new SubscriptionClient(options)
}
